import {ProjectFeatureFlag} from 'common/report/constants';

import {DateRangeType, TimeUnit} from './types';

export enum PresetDateRange {
  Custom = `custom`,
  Today = `today`,
  Yesterday = `yesterday`,
  Last1Day = `last_1_day`,
  Last96Hours = `last_96_hours`,
  Last7Days = `last_7_days`,
  Last30Days = `last_30_days`,
  Last3Months = `last_3_months`,
  Last24Weeks = `last_24_weeks`,
  Last6Months = `last_6_months`,
  Last12Months = `last_12_months`,
}

export const SHOW_LAST_X_DAYS_PRESET_DATE_RANGE = {
  [ProjectFeatureFlag.ShowLast1Day]: PresetDateRange.Last1Day,
  [ProjectFeatureFlag.ShowLast7Days]: PresetDateRange.Last7Days,
};

export const TIME_UNIT_DISPLAY_NAMES: {[key in TimeUnit]: string} = {
  [TimeUnit.Minute]: `minutes`,
  [TimeUnit.Hour]: `hours`,
  [TimeUnit.Day]: `days`,
  [TimeUnit.Week]: `weeks`,
  [TimeUnit.Month]: `months`,
  [TimeUnit.Quarter]: `quarters`,
  [TimeUnit.Year]: `years`,
  [TimeUnit.Session]: `sessions`,
};

export const DATE_RANGE_TYPE_DISPLAY_NAMES: {[key in DateRangeType]: string} = {
  [DateRangeType.Between]: `between`,
  [DateRangeType.NotBetween]: `not between`,
  [DateRangeType.On]: `on`,
  [DateRangeType.NotOn]: `not on`,
  [DateRangeType.RelativeAfter]: `in the last`,
  [DateRangeType.RelativeBefore]: `before the last`,
  [DateRangeType.Since]: `since`,
  [DateRangeType.StartsOn]: `starts on`,
  [DateRangeType.EndsOn]: `ends on`,
  [DateRangeType.RelativeFuture]: `in the next`,
  [DateRangeType.NotRelativeAfter]: `not in the`,
  [DateRangeType.Before]: `before`,
};

export const START_OF_YESTERDAY = `$start_of_previous_day`;
export const END_OF_YESTERDAY = `$end_of_previous_day`;
export const START_OF_TODAY = `$start_of_current_day`;
export const END_OF_TODAY = `$end_of_current_day`;
export const TODAY = `$today`;
export const NOW = `$now`;

export const PRESET_DATE_RANGE_DISPLAY_NAMES: {[key in PresetDateRange]: string} = {
  [PresetDateRange.Today]: `Current day`,
  [PresetDateRange.Yesterday]: `Previous day`,
  [PresetDateRange.Last1Day]: `Last 1 day`,
  [PresetDateRange.Last96Hours]: `Last 96 hours`,
  [PresetDateRange.Last7Days]: `Last 7 days`,
  [PresetDateRange.Last30Days]: `Last 30 days`,
  [PresetDateRange.Last3Months]: `Last 3 months`,
  [PresetDateRange.Last24Weeks]: `Last 24 weeks`,
  [PresetDateRange.Last6Months]: `Last 6 months`,
  [PresetDateRange.Last12Months]: `Last 12 months`,
  [PresetDateRange.Custom]: `Select a date range...`,
};

export const PRESET_DATE_RANGE_SHORT_DISPLAY_NAMES: {[key in PresetDateRange]: string} = {
  [PresetDateRange.Today]: `Today`,
  [PresetDateRange.Yesterday]: `Yesterday`,
  [PresetDateRange.Last1Day]: `1D`,
  [PresetDateRange.Last96Hours]: `96H`,
  [PresetDateRange.Last7Days]: `7D`,
  [PresetDateRange.Last30Days]: `30D`,
  [PresetDateRange.Last3Months]: `3M`,
  [PresetDateRange.Last24Weeks]: `24W`,
  [PresetDateRange.Last6Months]: `6M`,
  [PresetDateRange.Last12Months]: `12M`,
  [PresetDateRange.Custom]: `Select date`,
};

export const PRESET_DATE_RANGE_OPTIONS: PresetDateRange[] = [
  PresetDateRange.Today,
  PresetDateRange.Yesterday,
  PresetDateRange.Last1Day,
  PresetDateRange.Last96Hours,
  PresetDateRange.Last7Days,
  PresetDateRange.Last30Days,
  PresetDateRange.Last3Months,
  PresetDateRange.Last24Weeks,
  PresetDateRange.Last6Months,
  PresetDateRange.Last12Months,
  PresetDateRange.Custom,
];

export const PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS = {
  [PresetDateRange.Today]: {
    from: START_OF_TODAY,
    to: END_OF_TODAY,
    type: DateRangeType.Between,
  },
  [PresetDateRange.Yesterday]: {
    from: START_OF_YESTERDAY,
    to: END_OF_YESTERDAY,
    type: DateRangeType.Between,
  },
  [PresetDateRange.Last1Day]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Day,
      value: 1,
    },
  },
  [PresetDateRange.Last96Hours]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Hour,
      value: 96,
    },
  },
  [PresetDateRange.Last7Days]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Day,
      value: 7,
    },
  },
  [PresetDateRange.Last30Days]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Day,
      value: 30,
    },
  },
  [PresetDateRange.Last3Months]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Month,
      value: 3,
    },
  },
  [PresetDateRange.Last24Weeks]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Week,
      value: 24,
    },
  },
  [PresetDateRange.Last6Months]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Month,
      value: 6,
    },
  },
  [PresetDateRange.Last12Months]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Month,
      value: 12,
    },
  },
};

export const TIME_COMPARISON_OPTIONS = {
  PREVIOUS_DAY: `previous_day`,
  PREVIOUS_WEEK: `previous_week`,
  PREVIOUS_MONTH: `previous_month`,
  PREVIOUS_QUARTER: `previous_quarter`,
  PREVIOUS_YEAR: `previous_year`,
  CUSTOM: `custom`,
};
